import { useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import API_CONFIG from "../config/api";
import { useSelection } from "../SelectionContext";
import { useEcommercePermissions } from "../hooks/useEcommercePermissions";
import useBusinessResource from "../hooks/useBusinessResource";
import { BusinessScope, invalidateBusinessResources } from "../utils/businessResourceFreshness";
import { ConfigStore, normalizeConfigStore, StoreWarehouse } from "./storeConfigurationModel";

export function useStoreConfigurationScope() {
  const { selectedCompany, selectedWarehouse } = useSelection();
  const permissions = useEcommercePermissions();
  const client = useQueryClient();
  const scope = { userId: permissions.user?.id, companyId: selectedCompany, warehouseId: selectedWarehouse, moduleScope: "ecommerce" };
  const headers = { "X-Company-Id": String(selectedCompany || "") };
  return { scope, headers, permissions, client,
    invalidate: () => invalidateBusinessResources(client, scope, "store-configuration") };
}
export async function readConfigStore(id: number, scope: BusinessScope, signal?: AbortSignal): Promise<ConfigStore> {
  const { data } = await apiClient.get(API_CONFIG.apiUrl(`/ecommerce/stores/${id}`), { signal,
    headers: { "X-Company-Id": String(scope.companyId || "") } });
  const store = data.store && normalizeConfigStore(data.store);
  if (!store || store.id !== id || store.company_id !== Number(scope.companyId) || store.warehouse_id !== Number(scope.warehouseId)) {
    throw new Error("Cette boutique n'appartient plus à l'entreprise et à l'établissement sélectionnés.");
  }
  return store;
}
export function useConfigStores() {
  const { scope, permissions, headers } = useStoreConfigurationScope();
  return useBusinessResource<ConfigStore[]>({ ...scope, resource: "store-configuration-stores", enabled: permissions.canViewStores(), active: true,
    load: async (signal) => {
      const stores: ConfigStore[] = [];
      let page = 1; let total = 0;
      do {
        const { data } = await apiClient.get(API_CONFIG.apiUrl("/ecommerce/stores"), { signal, headers,
          params: { warehouse_id: scope.warehouseId, page, limit: 100 } });
        if (!Array.isArray(data.stores)) throw new Error("La liste des boutiques est indisponible.");
        stores.push(...data.stores.map(normalizeConfigStore).filter((store: ConfigStore) => store.company_id === Number(scope.companyId)
          && store.warehouse_id === Number(scope.warehouseId)));
        total = Number(data.pagination?.totalItems ?? data.stores.length);
        if (!data.stores.length) break;
        page += 1;
      } while ((page - 1) * 100 < total);
      return stores;
    },
  });
}
export function useConfigStore(id: number | null, active = true) {
  const { scope, permissions } = useStoreConfigurationScope();
  return useBusinessResource<ConfigStore>({ ...scope, resource: "store-configuration-detail", parameters: [id],
    enabled: permissions.canViewStores() && Boolean(id), active,
    load: (signal) => readConfigStore(Number(id), scope, signal),
  });
}
export function useStoreWarehouseChoices(active = true) {
  const { scope, permissions, headers } = useStoreConfigurationScope();
  return useBusinessResource<StoreWarehouse[]>({ ...scope, resource: "store-configuration-warehouses", scopeLevel: "company",
    enabled: permissions.canViewStores(), active,
    load: async (signal) => {
      const { data } = await apiClient.get(API_CONFIG.apiUrl("/ecommerce/stores/warehouses/available"), { signal, headers });
      if (!Array.isArray(data.warehouses)) throw new Error("Les établissements disponibles n'ont pas pu être chargés.");
      return data.warehouses.map((row: any) => ({ ...row, id: Number(row.id), company_id: Number(row.company_id) }))
        .filter((row: StoreWarehouse) => row.company_id === Number(scope.companyId));
    },
  });
}
